import React, { useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Tabs, Tab, Form } from "react-bootstrap";
import { Modal } from "react-responsive-modal";
import { useToasts } from "react-toast-notifications";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faSave, faPlus, faTrash, 
  faArrowUp, faArrowDown
} from "@fortawesome/free-solid-svg-icons";
import * as Yup from "yup";
import { useFormik } from "formik";
import Loader from "components/Loader";
import Button from "components/CustomButton";
import ChatGptAssistModal from "components/ChatGptAssistModal";
import CourseModuleEditor from "components/CourseBuilder/CourseModuleEditor/CourseModuleEditor";
import ThumbnailUpload from "components/ThumbnailUpload/ThumbnailUpload";
import CourseService from "services/CourseService";
import { ReactComponent as CloseIcon } from "assets/img/icons/cancel.svg";

const TeamCourseCreator = () => {
  const { teamId } = useParams();
  const history = useHistory();
  const { addToast } = useToasts();
  const [key, setKey] = useState("details");
  const [loading, setLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedModuleIndex, setSelectedModuleIndex] = useState(null);
  const [coverAttachment, setCoverAttachment] = useState(null);
  
  const [course, setCourse] = useState({
    title: "",
    description: "",
    status: "draft",
    modules: [],
    thumbnail: ""
  });

  const validationSchema = Yup.object().shape({
    title: Yup.string()
      .min(4, "Course title must be between 4 to 100 characters.")
      .max(100, "Course title must be between 4 to 100 characters.")
      .trim()
      .required("Please enter the course title."),
    description: Yup.string()
      .trim()
      .required("Please enter the course description."),
    thumbnail: Yup.string()
      .required("Please select a thumbnail for the course.")
  });

  const formik = useFormik({
    initialValues: {
      title: course.title,
      description: course.description,
      thumbnail: course.thumbnail
    },
    validationSchema: validationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    enableReinitialize: true,
    onSubmit: async (values) => {
      await handleSaveCourse();
    }
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCourse((prev) => ({
      ...prev,
      [name]: value
    }));
    formik.setFieldValue(name, value);
  };

  const validateFields = () => {
    if (!course.title || course.title.trim() === "") {
      setKey("details");
      return false;
    }

    if (!course.description || course.description.trim() === "") {
      setKey("details");
      return false;
    }

    if (!course.thumbnail || (typeof course.thumbnail === 'string' && course.thumbnail.trim() === "")) {
      setKey("details");
      return false;
    }

    if (course.modules.length === 0) {
      addToast("Please add at least one module to the course.", { appearance: "error" });
      setKey("modules");
      return false;
    }

    for (const module of course.modules) {
      if (!module.title || module.title.trim() === "") {
        addToast("All modules must have a title.", { appearance: "error" });
        setKey("modules");
        return false;
      }

      if (!module.lessons || module.lessons.length === 0) {
        addToast(`Module "${module.title}" must have at least one lesson.`, { appearance: "error" });
        setKey("modules");
        return false;
      }

      for (const lesson of module.lessons) {
        if (!lesson.title || lesson.title.trim() === "") {
          addToast(`All lessons in module "${module.title}" must have a title.`, { appearance: "error" });
          setKey("modules");
          return false;
        }

        if (!lesson.content || (typeof lesson.content === 'string' && lesson.content.trim() === "" && lesson.type !== "video")) {
          addToast(`All lessons in module "${module.title}" must have content.`, { appearance: "error" });
          setKey("modules");
          return false;
        }
        
        if (lesson.type === "video" && !lesson.content) {
          addToast(`Video lesson "${lesson.title}" in module "${module.title}" must have a video file or URL.`, { appearance: "error" });
          setKey("modules");
          return false;
        }

        if (lesson.duration === '' || lesson.duration === null || lesson.duration === undefined) {
          addToast(`Lesson "${lesson.title}" in module "${module.title}" must have a duration.`, { appearance: "error" });
          setKey("modules");
          return false;
        }

        if (Number(lesson.duration) < 1) {
          addToast(`Lesson "${lesson.title}" in module "${module.title}" must have a duration of at least 1 minute.`, { appearance: "error" });
          setKey("modules");
          return false;
        }
        
        if (Number(lesson.duration) > 999) {
          addToast(`Lesson "${lesson.title}" in module "${module.title}" has a duration exceeding the maximum of 999 minutes.`, { appearance: "error" });
          setKey("modules");
          return false;
        }
      }
    }

    return true;
  };

  const handleSaveCourse = async () => {
    try {
      await formik.validateForm();
      if (Object.keys(formik.errors).length > 0) {
        formik.setTouched({
          title: true,
          description: true,
          thumbnail: true
        });
        formik.handleSubmit();
        return;
      }

      if (!validateFields()) {
        return;
      }

      setLoading(true);

      const formData = new FormData();
      
      formData.append("title", course.title);
      formData.append("description", course.description);
      formData.append("team_id", parseInt(teamId));
      formData.append("status", course.status);
      
      if (course.thumbnail && typeof course.thumbnail === 'object') {
        formData.append("thumbnail", course.thumbnail);
      }
      
      course.modules.forEach((module, moduleIndex) => {
        formData.append(`modules[${moduleIndex}][title]`, module.title);
        formData.append(`modules[${moduleIndex}][description]`, module.description || "");
        formData.append(`modules[${moduleIndex}][order]`, moduleIndex);
        
        module.lessons.forEach((lesson, lessonIndex) => {
          formData.append(`modules[${moduleIndex}][lessons][${lessonIndex}][title]`, lesson.title);
          formData.append(`modules[${moduleIndex}][lessons][${lessonIndex}][type]`, lesson.type || "text");
          formData.append(`modules[${moduleIndex}][lessons][${lessonIndex}][cta]`, lesson.cta || "");
          formData.append(`modules[${moduleIndex}][lessons][${lessonIndex}][duration]`, lesson.duration === '' ? 1 : (lesson.duration || 1));
          formData.append(`modules[${moduleIndex}][lessons][${lessonIndex}][order]`, lessonIndex);
          formData.append(`modules[${moduleIndex}][lessons][${lessonIndex}][review]`, lesson.review === undefined ? true : lesson.review);
          
          if (lesson.type === 'video') {
            // For uploaded videos, add the content as a file
            if (lesson.content instanceof File) {
              formData.append(`modules[${moduleIndex}][lessons][${lessonIndex}][content]`, lesson.content);
            } else if (typeof lesson.content === 'string') {
              formData.append(`modules[${moduleIndex}][lessons][${lessonIndex}][content]`, lesson.content || '');
            } else {
              // Fallback to empty string if no content is available
              formData.append(`modules[${moduleIndex}][lessons][${lessonIndex}][content]`, '');
            }
          } else {
            // For non-video lessons, handle content normally
            formData.append(`modules[${moduleIndex}][lessons][${lessonIndex}][content]`, lesson.content || "");
          }
          
          if (lesson.attachments && lesson.attachments.length > 0) {
            lesson.attachments.forEach((attachment, attachmentIndex) => {
              formData.append(`modules[${moduleIndex}][lessons][${lessonIndex}][attachments][${attachmentIndex}]`, attachment);
            });
          }
        });
      });

      await CourseService.createCourse(formData);

      formik.resetForm();
      
      addToast("Course created successfully.", { appearance: "success" });
      history.push(`/teams/${teamId}/coursebuilder`);
    } catch (error) {
      console.error("Error saving course:", error);
      addToast(error.response?.data?.message || "Failed to create course. Please try again.", { appearance: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleAddModule = () => {
    const newModule = {
      id: Date.now(),
      title: "New Module",
      description: "",
      order: course.modules.length + 1,
      lessons: []
    };
    
    setCourse((prev) => ({
      ...prev,
      modules: [...prev.modules, newModule]
    }));
    
    setKey("modules");
    setSelectedModuleIndex(course.modules.length);
    
    setTimeout(() => {
      const moduleListItems = document.querySelectorAll('.module-list .list-group-item');
      if (moduleListItems.length > 0) {
        const lastModuleItem = moduleListItems[moduleListItems.length - 1];
        lastModuleItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  const handleDeleteModule = (index) => {
    setSelectedModuleIndex(index);
    setShowConfirmModal(true);
  };

  const confirmDeleteModule = () => {
    setCourse((prev) => ({
      ...prev,
      modules: prev.modules.filter((_, i) => i !== selectedModuleIndex)
    }));
    setShowConfirmModal(false);
    setSelectedModuleIndex(null);
  };

  const handleMoveModule = (index, direction) => {
    if (
      (direction === "up" && index === 0) || 
      (direction === "down" && index === course.modules.length - 1)
    ) {
      return;
    }

    const newModules = [...course.modules];
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    
    const temp = newModules[index];
    newModules[index] = newModules[targetIndex];
    newModules[targetIndex] = temp;
    
    newModules.forEach((module, i) => {
      module.order = i + 1;
    });
    
    setCourse((prev) => ({
      ...prev,
      modules: newModules
    }));
    
    setSelectedModuleIndex(targetIndex);
  };

  const handleUpdateModule = (updatedModule, index) => {
    setCourse((prev) => {
      const updatedModules = [...prev.modules];
      updatedModules[index] = updatedModule;
      return {
        ...prev,
        modules: updatedModules
      };
    });
  };

  const handleCoverPhotoChange = (file) => {
    setCoverAttachment(file);
    setCourse((prev) => ({ ...prev, thumbnail: file }));
    formik.setFieldValue("thumbnail", file);
  };
  
  const clearCoverPhoto = () => {
    setCoverAttachment(null);
    setCourse((prev) => ({ ...prev, thumbnail: "" }));
    formik.setFieldValue("thumbnail", "");
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <Loader />
      </div>
    );
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3>Create Course</h3>
        <div className="d-flex gap-2 mt-2">
          <Button buttonClass="btn-secondary mb-3" onClick={() => history.push(`/teams/${teamId}/coursebuilder`)}>
            Cancel
          </Button>
          <Button buttonClass="grad_btn d-inline-block mb-3" onClick={formik.handleSubmit}>
            <FontAwesomeIcon icon={faSave} className="me-2" />
            Save Course
          </Button>
        </div>
      </div>
      
      <div className="course-tabs">
        <Tabs
          activeKey={key}
          onSelect={(k) => setKey(k)}
          className="mb-4"
        >
          <Tab eventKey="details" title="Course Details">
            <div className="card">
              <div className="card-body">
                <Form noValidate onSubmit={formik.handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label htmlFor="title">Course Title</Form.Label>
                    <Form.Control
                      type="text"
                      id="title"
                      name="title"
                      value={course.title}
                      onChange={handleInputChange}
                      placeholder="Enter course title"
                      isInvalid={formik.touched.title && formik.errors.title}
                      required
                    />
                    {formik.touched.title && formik.errors.title && (
                      <Form.Control.Feedback type="invalid">
                        {formik.errors.title}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>
                  
                  <Form.Group className="mb-3">
                    <Form.Label htmlFor="description">Description</Form.Label>
                    <div className="position-relative">
                      <ChatGptAssistModal setValue={(value) => {
                        setCourse(prev => ({ ...prev, description: value }));
                        formik.setFieldValue("description", value);
                      }} />
                      <Form.Control
                        as="textarea"
                        id="description"
                        name="description"
                        value={course.description}
                        onChange={handleInputChange}
                        rows="4"
                        placeholder="Describe what this course is about"
                        isInvalid={formik.touched.description && formik.errors.description}
                      />
                      {formik.touched.description && formik.errors.description && (
                        <Form.Control.Feedback type="invalid">
                          {formik.errors.description}
                        </Form.Control.Feedback>
                      )}
                    </div>
                  </Form.Group>

                  {/* Thumbnail Upload */}
                  <Form.Group className="mb-3">
                    <Form.Label>Thumbnail <span className="text-danger">*</span></Form.Label>
                    <div className="mb-2">
                      <ThumbnailUpload
                        thumbnail={coverAttachment}
                        onThumbnailChange={handleCoverPhotoChange}
                        onThumbnailRemove={clearCoverPhoto}
                      />
                    </div>
                    {formik.touched.thumbnail && formik.errors.thumbnail && (
                      <div className="text-danger mt-1">
                        {formik.errors.thumbnail}
                      </div>
                    )}
                  </Form.Group>
                </Form>
              </div>
            </div>
          </Tab>

          <Tab eventKey="modules" title="Course Modules">
            <div className="row">
              <div className="col-md-5 sticky-modules-column">
                <div className="card">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="mb-0">Modules</h5>
                    </div>
                    <div className="mb-3">
                      <Button variant="primary" size="sm" onClick={handleAddModule}>
                        <FontAwesomeIcon icon={faPlus} className="me-1" />
                        Add Module
                      </Button>
                    </div>
                    
                    {course.modules.length === 0 ? (
                      <p className="text-muted text-center py-3">No modules added yet</p>
                    ) : (
                      <ul className="list-group module-list">
                        {course.modules.map((module, index) => (
                          <li 
                            key={module.id} 
                            className={`list-group-item d-flex justify-content-between align-items-center ${selectedModuleIndex === index ? 'active' : ''}`}
                            onClick={() => {
                              setSelectedModuleIndex(index);
                              const moduleEditorTabs = document.querySelector('.course-module-editor .nav-tabs');
                              if (moduleEditorTabs) {
                                const basicTab = moduleEditorTabs.querySelector('button[data-rr-ui-event-key="basic"]');
                                if (basicTab) {
                                  basicTab.click();
                                }
                              }
                            }}
                          >
                            <div>
                              <div className="fw-bold">{module.title}</div>
                              <small className="text-muted">{module.description}</small>
                            </div>
                            <div className="module-controls">
                              <Button
                                variant="link" 
                                size="sm" 
                                buttonClass="text-secondary p-1"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMoveModule(index, "up");
                                }}
                                disabled={index === 0}
                              >
                                <FontAwesomeIcon icon={faArrowUp} />
                              </Button>
                              <Button 
                                variant="link" 
                                size="sm" 
                                buttonClass="text-secondary p-1"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMoveModule(index, "down");
                                }}
                                disabled={index === course.modules.length - 1}
                              >
                                <FontAwesomeIcon icon={faArrowDown} />
                              </Button>
                              <Button 
                                variant="link" 
                                size="sm" 
                                buttonClass="text-danger p-1"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteModule(index);
                                }}
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </Button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="col-md-7 lessons-column">
                {selectedModuleIndex !== null && (
                  <div className="card">
                    <div className="card-body">
                      <CourseModuleEditor
                        module={course.modules[selectedModuleIndex]}
                        onChange={(updatedModule) => handleUpdateModule(updatedModule, selectedModuleIndex)}
                      />
                    </div>
                  </div>
                )}
                {selectedModuleIndex === null && course.modules.length > 0 && (
                  <div className="card">
                    <div className="card-body text-center text-muted py-5">
                      <p>Select a module to edit its content</p>
                    </div>
                  </div>
                )}
                {course.modules.length === 0 && (
                  <div className="card">
                    <div className="card-body text-center text-muted py-5">
                      <p>Add your first module to get started</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Tab>
        </Tabs>
      </div>

      {/* Confirmation Modal for Module Deletion */}
      <Modal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        showCloseIcon={false}
        closeOnEsc={false}
        closeOnOverlayClick={false}
        classNames={{
          modalContainer: "modalContainer",
          modal: "createCourseModal",
        }}
      >
        <div className="modal fade popup-modal create-task-popup">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="text-center createTaskHeading">Delete Module</h5>
            <CloseIcon style={{ width: "20px", cursor: "pointer" }} onClick={() => setShowConfirmModal(false)} />
          </div>
          <div className="mb-4">
            <p>Are you sure you want to delete this Module? This action cannot be undone.</p>
          </div>
          <div className="d-flex justify-content-end">
            <Button buttonClass="btn-secondary me-2" onClick={() => setShowConfirmModal(false)}>
              Cancel
            </Button>
            <Button buttonClass="btn-danger" onClick={confirmDeleteModule}>
              Delete
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default TeamCourseCreator; 